import { useEffect, useRef } from "react";
import { useRevalidator } from "react-router";
import { nextSwitchBoundary, parseSwitchDay } from "~/utils/organisation-switch";

// FOX-6291 Part A: the root loader recomputes the switch phase on every load,
// but a tab left open across the switch day, or past the Moved window's end,
// loads nothing. Revalidate once each time a boundary is crossed: when the
// timer reaches it, or when the tab comes back after it passed unseen.

/** setTimeout's delay is a 32-bit int; wait at most a day, then re-arm. */
const MAX_WAIT_MS = 24 * 60 * 60 * 1000;

export function useSwitchBoundaryRevalidation(switchDayValue: string | null): void {
  const revalidator = useRevalidator();
  const revalidate = useRef(revalidator.revalidate);
  revalidate.current = revalidator.revalidate;

  useEffect(() => {
    const switchDay = parseSwitchDay(switchDayValue);
    if (!switchDay) return;
    const boundaryNow = () => nextSwitchBoundary(switchDay, new Date())?.getTime() ?? null;
    let pending = boundaryNow();
    let timer: number | undefined;

    // A new upcoming boundary means the previous one was crossed.
    const checkCrossed = () => {
      const next = boundaryNow();
      if (next === pending) return;
      pending = next;
      void revalidate.current();
    };
    const arm = () => {
      if (pending === null) return;
      timer = window.setTimeout(
        () => {
          checkCrossed();
          arm();
        },
        Math.min(Math.max(pending - Date.now(), 0), MAX_WAIT_MS),
      );
    };
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      checkCrossed();
      window.clearTimeout(timer);
      arm();
    };

    arm();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [switchDayValue]);
}
